import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ClientContainer } from '@/providers';
import { useOccasionalDiscount } from '@/hooks/useOccasionalDiscount';
import { BANNER_REGISTRY } from '@/config/bannerRegistry';
import type { BannerEvent } from '@/config/bannerRegistry';
import { usePostHog } from 'posthog-js/react';
import useLocalStorage from '@/hooks/localstorage';

// Storage value structure for dismissal state
interface DismissalState {
  dismissed: boolean;
  timestamp: number;
}

const DISMISSAL_EXPIRY_DAYS = 7; // Configurable expiration period
const DISMISSAL_EXPIRY_MS = DISMISSAL_EXPIRY_DAYS * 24 * 60 * 60 * 1000;

export const DiscountBanner: React.FC = () => {
  // ALL HOOKS MUST BE AT THE TOP - before any conditional returns
  const { state: clientState } = ClientContainer.useContainer();
  const {
    showOccasionalDiscount,
    maxDiscount,
    disableDiscountDecorations,
  } = useOccasionalDiscount();
  const posthog = usePostHog();
  const mountTimeRef = useRef<number>(Date.now());
  const { get, set: setStorage } = useLocalStorage();

  const { activeEvent } = showOccasionalDiscount;

  // Per-event dismissal state with expiration
  // Use activeEvent or fallback to prevent invalid storage keys
  const storageKey = `discountBanner_${activeEvent || 'default'}_dismissed`;

  // Initialize state from localStorage
  const [dismissalState, setDismissalState] = useState<DismissalState | null>(
    () => {
      try {
        const stored = get(storageKey);
        if (
          stored &&
          typeof stored === 'object' &&
          'dismissed' in stored &&
          'timestamp' in stored
        ) {
          return stored as DismissalState;
        }
        return null;
      } catch {
        return null;
      }
    }
  );

  // Reload state from localStorage when storage key changes
  useEffect(() => {
    try {
      const stored = get(storageKey);
      if (
        stored &&
        typeof stored === 'object' &&
        'dismissed' in stored &&
        'timestamp' in stored
      ) {
        setDismissalState(stored as DismissalState);
      } else {
        setDismissalState(null);
      }
    } catch {
      setDismissalState(null);
    }
  }, [storageKey, get]);

  // Sync state changes to localStorage
  useEffect(() => {
    if (dismissalState === null) {
      // Remove from storage if setting to null
      try {
        localStorage.removeItem(storageKey);
      } catch {
        // Ignore errors
      }
    } else {
      // Store the dismissal state
      setStorage(storageKey, dismissalState);
    }
  }, [dismissalState, storageKey, setStorage]);

  // Check if dismissal is still valid
  const isDismissed = useMemo(() => {
    if (!dismissalState || !dismissalState.dismissed) {
      return false;
    }

    const timeSinceDismissal = Date.now() - dismissalState.timestamp;
    const isExpired = timeSinceDismissal > DISMISSAL_EXPIRY_MS;

    if (isExpired) {
      // Clear expired dismissal
      setDismissalState(null);
      return false;
    }

    return true;
  }, [dismissalState, setDismissalState]);

  // // Track banner view on mount
  // Disabled for now
  // useEffect(() => {
  //   if (posthog && activeEvent && !isDismissed) {
  //     posthog.capture('discount_banner_viewed', {
  //       event: activeEvent,
  //       maxDiscount,
  //       timestamp: Date.now(),
  //     });
  //   }
  // }, [posthog, activeEvent, isDismissed, maxDiscount]);

  const clientId = clientState?.selectedClient?.id;
  const redirectPath = `/ws/${clientId}/settings/billing?plan=team`;

  // Check if user is a plus annual user - don't show banner for them
  if (disableDiscountDecorations) {
    return null;
  }

  // Manage own visibility state
  if (!showOccasionalDiscount.showInAppDiscountBanner) {
    return null;
  }

  if (!activeEvent) {
    return null;
  }

  // Check dismissal state
  if (isDismissed) {
    return null;
  }

  const BannerComponent = BANNER_REGISTRY[activeEvent as BannerEvent];

  if (!BannerComponent) {
    // Event exists but no banner component registered
    // eslint-disable-next-line no-console
    console.warn(`No banner component found for event: ${activeEvent}`);
    return null;
  }

  // Close handler with analytics tracking
  const handleClose = () => {
    const timeVisible = Date.now() - mountTimeRef.current;

    // Track dismissal with timing
    if (posthog) {
      posthog.capture('discount_banner_dismissed', {
        event: activeEvent,
        maxDiscount,
        timeVisibleMs: timeVisible,
        timeVisibleSeconds: Math.round(timeVisible / 1000),
        expiryDays: DISMISSAL_EXPIRY_DAYS,
      });
    }

    // Store with timestamp
    setDismissalState({
      dismissed: true,
      timestamp: Date.now(),
    });
  };

  return (
    <BannerComponent
      maxDiscount={maxDiscount}
      redirectPath={redirectPath}
      onClose={handleClose}
    />
  );
};
